import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import PerfectScrollbarJS from 'perfect-scrollbar';
import { style, classes } from 'typestyle';

export interface PerfectScrollbarHandle {
  scrollTop(value: number): void;
  update(): void;
}

interface IProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onScroll'> {
  options?: PerfectScrollbarJS.Options
  className?: string
  id?: string
  style?: any
  containerRef?: React.RefObject<HTMLDivElement | null>
  onScroll?: React.UIEventHandler<HTMLDivElement>
}

export const PerfectScrollbar = forwardRef<PerfectScrollbarHandle, IProps>(
  function PerfectScrollbar({ children, containerRef, className, onScroll, options, ...props }, ref) {
    const internalRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<PerfectScrollbarJS | undefined>(undefined);
    const onScrollRef = useRef(onScroll);
    onScrollRef.current = onScroll;

    const getEl = () => containerRef?.current ?? internalRef.current;

    useImperativeHandle(ref, () => ({
      scrollTop(value: number) {
        const el = getEl();
        if (el) {
          el.scrollTop = value;
          barRef.current?.update();
        }
      },
      update() {
        barRef.current?.update();
      },
    }));

    useEffect(() => {
      const component = getEl();
      if (!component) {
        console.warn('perfectscrollbar not properly created');
        return;
      }
      barRef.current = new PerfectScrollbarJS(component, options);

      const scrollHandler: EventListener = (event: Event) => {
        if (!onScrollRef.current) return;
        const syntheticEvent = {
          ...event,
          currentTarget: event.currentTarget as HTMLDivElement,
          target: event.target as HTMLDivElement,
          detail: 0,
          view: window as any,
          nativeEvent: event,
          preventDefault: () => event.preventDefault(),
          stopPropagation: () => event.stopPropagation(),
          isDefaultPrevented: () => event.defaultPrevented,
          isPropagationStopped: () => false,
          persist: () => {},
        } as unknown as React.UIEvent<HTMLDivElement>;
        onScrollRef.current(syntheticEvent);
      };
      component.addEventListener('scroll', scrollHandler);

      return () => {
        component.removeEventListener('scroll', scrollHandler);
        barRef.current?.destroy();
        barRef.current = undefined;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      barRef.current?.update();
    });

    return (
      <div ref={containerRef ?? internalRef} className={classes(scrollbarClass, className)} {...props}>
        {children}
      </div>
    );
  },
);

const scrollbarClass = style({
  position: 'relative',
});
